import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { ParticipantFieldScope, TicketAttribute } from '__generated__/graphql';

import { DateTimeForm } from '../../../Common/DateRangeForm';
import { Event } from './helpers';
import { ImageInput } from '../../../../common/Common/UploadButton';
import { getServerErrors } from '../../../../common/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

const editEventTicketSettingsSchema = yup.object({});

export interface EditEventTicketSettingsFormProps {
  event: Event;
}

const ticketAttributes = [
  TicketAttribute.name,
  TicketAttribute.email,
  TicketAttribute.date_of_birth,
  TicketAttribute.gender,
  TicketAttribute.nationality,
  TicketAttribute.address,
  TicketAttribute.phone,
  TicketAttribute.emergency_phone,
];

const EditEventTicketSettingsForm = ({ event }: EditEventTicketSettingsFormProps) => {
  const { t } = useTranslation();

  const getFields = (event: Event) => event.participant_fields.filter(
    (field) => field.scope === ParticipantFieldScope.purchase,
  );

  const getValues = (event: Event) => ({
    enable_tickets: event.enable_tickets,
    ticket_image: null as any,
    delete_ticket_image: false,
    ticket_attributes: event.ticket_attributes,
    ticket_fields: getFields(event).filter((field) => field.show_on_ticket).map((field) => field.id),
    tickets_available_from: event.tickets_available_from,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      tickets: form.values,
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventTicketSettingsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('ticket_pdfs')}</UI.Legend>
          <UI.InputDescription>
            {t('event_tickets_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup>
          <UI.InputLabel>{t('enable_tickets')}</UI.InputLabel>
          <UI.InputDescription>
            {t('enable_tickets_tickets_description')}
          </UI.InputDescription>
          <UI.Checkbox
            onChange={() => form.set((values) => ({ ...values, enable_tickets: !values.enable_tickets }))}
            checked={form.values.enable_tickets}
          >
            {t('allow_downloading_tickets')}
          </UI.Checkbox>
        </UI.InputGroup>

        {form.values.enable_tickets && (
          <>
            <DateTimeForm
              form={form}
              errors={errors}
              keys={{ date: 'tickets_available_from', time: 'time' }}
            />

            <UI.InputGroup>
              <UI.InputLabel>
                {t('visible_on_ticket')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('ticket_fields_description')}
              </UI.InputDescription>
              {ticketAttributes.map((attribute) => (
                <UI.Checkbox
                  {...form.checklist('ticket_attributes', attribute)}
                  key={attribute}
                >
                  {t(attribute)}
                </UI.Checkbox>
              ))}
              {getFields(event).map((field) => (
                <UI.Checkbox
                  {...form.checklist('ticket_fields', field.id)}
                  key={field.id}
                >
                  {field.title}
                </UI.Checkbox>
              ))}
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.ticket_image, touched: form.touched.ticket_image }}>
              <UI.InputLabel>
                {t('ticket_image')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('ticket_image_description')}
              </UI.InputDescription>
              <ImageInput
                value={form.values.ticket_image}
                deleteImage={form.values.delete_ticket_image}
                onChange={(file) => {
                  form.set('ticket_image', file);
                  form.set('delete_ticket_image', !file);
                }}
                previewImage={event.ticket_image?.url}
                imageAlt={event.title}
                width={600}
                ratio={3}
              />
              <UI.ErrorMessages attribute={t('ticket_image')} errors={errors.ticket_image} />
            </UI.InputGroup>
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventTicketSettingsForm;
